import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  fetchQueue,
  logFollowUp,
  assignSponsors,
  fetchHistory,
  fetchFollowUpUsers,
  fetchFollowUpMeta,
  fetchCalendarData,
  fetchAnalytics,
  fetchAnalyticsDetail,
  runCleanup,
  checkMissedInstalments,
  deleteFollowUpLog,
  clearStaleFollowUp,
} from "./api";
import { followUpKeys } from "./keys";
import { toast } from "sonner";

const RETRY_OPTS = { retry: 2, retryDelay: 1000 };

export const useFollowUpQueue = (filters = {}) => {
  return useQuery({
    queryKey: followUpKeys.queue(filters),
    queryFn: () => fetchQueue(filters),
    staleTime: 1000 * 60 * 2,
    ...RETRY_OPTS,
  });
};

export const useLogFollowUp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logFollowUp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: followUpKeys.all });
      queryClient.invalidateQueries({ queryKey: ["sponsorships"] });
      toast.success("Follow-up logged successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to log follow-up");
    },
  });
};

export const useAssignSponsors = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: assignSponsors,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: followUpKeys.all });
      toast.success(data.message || "Sponsors assigned successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to assign sponsors");
    },
  });
};

export const useFollowUpHistory = (sponsorshipId) => {
  return useQuery({
    queryKey: followUpKeys.history(sponsorshipId),
    queryFn: () => fetchHistory(sponsorshipId),
    enabled: !!sponsorshipId,
    ...RETRY_OPTS,
  });
};

export const useFollowUpUsers = () => {
  return useQuery({
    queryKey: ["followUpUsers"],
    queryFn: fetchFollowUpUsers,
    staleTime: 1000 * 60 * 5,
    ...RETRY_OPTS,
  });
};

export const useFollowUpMeta = (params) => {
  return useQuery({
    queryKey: followUpKeys.meta(params),
    queryFn: () => fetchFollowUpMeta(params),
    staleTime: 1000 * 60 * 5,
    enabled: params !== null,
    ...RETRY_OPTS,
  });
};

export const useInfiniteFollowUpQueue = (filters = {}) => {
  return useInfiniteQuery({
    queryKey: followUpKeys.queue(filters),
    queryFn: ({ pageParam = 1 }) => fetchQueue({ ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pages } = lastPage.pagination || {};
      return page < pages ? page + 1 : undefined;
    },
    staleTime: 1000 * 60 * 2,
    ...RETRY_OPTS,
  });
};

export const useFollowUpCalendar = (params) => {
  return useQuery({
    queryKey: followUpKeys.calendar(params),
    queryFn: () => fetchCalendarData(params),
    enabled: !!params?.from && !!params?.to,
    staleTime: 1000 * 60 * 2,
    ...RETRY_OPTS,
  });
};

export const useFollowUpAnalytics = (params) => {
  return useQuery({
    queryKey: followUpKeys.analytics(params),
    queryFn: () => fetchAnalytics(params),
    staleTime: 1000 * 60 * 2,
    ...RETRY_OPTS,
  });
};

export const useFollowUpAnalyticsDetail = (params, enabled = false) => {
  return useQuery({
    queryKey: followUpKeys.analyticsDetail(params),
    queryFn: () => fetchAnalyticsDetail(params),
    enabled,
    staleTime: 1000 * 60 * 2,
    ...RETRY_OPTS,
  });
};

export const useRunCleanup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runCleanup,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: followUpKeys.all });
      toast.success(data.message || "Cleanup completed");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Cleanup failed");
    },
  });
};

export const useCheckMissedInstalments = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: checkMissedInstalments,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: followUpKeys.all });
      toast.success(data.message || "Missed instalments check completed");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to check missed instalments",
      );
    },
  });
};

export const useDeleteFollowUpLog = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteFollowUpLog,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: followUpKeys.all });
      queryClient.invalidateQueries({ queryKey: ["sponsorships"] });
      toast.success("Log deleted");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete log");
    },
  });
};

export const useClearStaleFollowUp = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clearStaleFollowUp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: followUpKeys.all });
      queryClient.invalidateQueries({ queryKey: ["sponsorships"] });
      toast.success("Cleared successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to clear");
    },
  });
};
